import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Flame } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * StreakBadge - shows the user's current daily practice streak.
 * Props:
 *   - refreshKey (any): change this value to trigger a refetch
 *   - onClick: optional click handler
 *   - compact: if true, renders compact version
 */
const StreakBadge = ({ refreshKey, onClick, compact = false }) => {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const data = await api.getStreak();
        if (!cancelled) setStreak(data);
      } catch (error) {
        console.error('Error fetching streak:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (loading || !streak) return null;

  const isActive = streak.current_streak > 0;
  const practicedToday = streak.practiced_today;

  return (
    <motion.button
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full font-bold transition-all ${
        compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
      } ${
        isActive
          ? practicedToday
            ? 'bg-gradient-to-r from-orange-100 to-amber-100 text-orange-700 border-2 border-orange-300'
            : 'bg-gradient-to-r from-slate-100 to-slate-50 text-slate-700 border-2 border-slate-300'
          : 'bg-slate-100 text-slate-500 border-2 border-slate-200'
      }`}
      data-testid="streak-badge"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={practicedToday ? 'lit' : 'dim'}
          initial={{ rotate: -30, scale: 0.8 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <Flame
            className={`${compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${
              isActive && practicedToday ? 'fill-orange-500 text-orange-600' : 'text-slate-400'
            }`}
            strokeWidth={2.5}
          />
        </motion.div>
      </AnimatePresence>
      <span data-testid="streak-count">
        {streak.current_streak} <span className="opacity-70">day{streak.current_streak === 1 ? '' : 's'}</span>
      </span>
    </motion.button>
  );
};

export default StreakBadge;
